import { useTheme } from "@/design";
import { isEqual } from "lodash";
import {
  useCreateStoreProfileMutation,
  useStoreSelfQuery,
  useUpdateStoreProfileMutation,
  useStoreListQuery,
} from "@/features/business";
import { useChangeProfileMutation, currentUserSelector } from "@/features/auth";
import { useSelector } from "react-redux";
import { OrientationEnum, defaultBarcodeConfig } from "@/features/product/config/barcode";
import {
  BillQrCodeEnum,
  ChoiceEnum,
  PrintSettingsFormData,
  StoreSelfResponse,
} from "@/features/settings/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  FileUploader,
  FileUploaderRef,
} from "@/components/form/file-upload/fileUploader";
import { FileTypeEnum } from "@/enum";
import { BankDetailsSection } from "@/features/payment";
import { appColors } from "@/constants/color";
import { StaffManagementScreen } from "../staff";
import { NativePrinterSection } from "@/features/settings/components/NativePrinterSection";

export const Route = createFileRoute("/(app)/(store)/settings/")({
  component: RouteComponent,
});

type SettingsSectionId =
  | "staff"
  | "bank"
  | "order"
  | "print"
  | "barcode"
  | "printer";

function RouteComponent() {
  return <GeneralSettingScreen />;
}
const defaultBarcodeConfigPayload = {
  stickerWidth: defaultBarcodeConfig.width,
  stickerHeight: defaultBarcodeConfig.height,
  stickersPerRow: defaultBarcodeConfig.stickersPerRow,
  orientation: defaultBarcodeConfig.orientation,
  logoUrl: "",
  useSameLogo: false,
  includeProductCode: defaultBarcodeConfig.includeProductCode,
  includeProductName: defaultBarcodeConfig.includeProductName,
  includeStoreName: defaultBarcodeConfig.includeStoreName,
  includeSellingPrice: defaultBarcodeConfig.includeSellingPrice,
  includeMrp: defaultBarcodeConfig.includeMrp,
};

// My Stores Section Component
const MyStoresSection = ({ theme }: { theme: any }) => {
  const navigate = useNavigate();
  const currentUser = useSelector(currentUserSelector);
  const { data: storeList, isLoading } = useStoreListQuery("");
  const [changeProfile, { isLoading: isSwitching }] = useChangeProfileMutation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSwitchStore = async (storeId: string) => {
    if (currentUser?._id === storeId || isSwitching) return;
    
    try {
      await changeProfile({ storeId, self: false }).unwrap();
      toast.success("Store switched successfully!");
      navigate({ to: "/dashboard" });
    } catch (error) {
      toast.error("Failed to switch store");
    }
  };

  if (isLoading) {
    return null;
  }

  // Show only first 2 stores unless expanded
  const visibleStores = isExpanded ? storeList : storeList?.slice(0, 2);
  const hasMoreStores = storeList && storeList.length > 2;

  return (
    <div
      style={{
        margin: theme.spacing.md,
        marginTop: 0,
        marginBottom: theme.spacing.md,
        backgroundColor: theme.colors.card,
        borderRadius: theme.radius.lg,
        padding: theme.spacing.md,
        boxShadow: `0 2px 8px ${theme.colors.border}20`,
        border: `1px solid ${theme.colors.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: theme.spacing.md,
        }}
      >
        <h2
          style={{
            fontSize: theme.fontSize.h4,
            fontWeight: 600,
            margin: 0,
            color: theme.colors.text,
          }}
        >
          My Stores
        </h2>
        <button
          onClick={() => navigate({ to: "/store/create" })}
          style={{
            border: "none",
            cursor: "pointer",
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
            borderRadius: theme.radius.md,
            backgroundColor: `${theme.colors.primary}12`,
            color: theme.colors.primary,
            fontSize: theme.fontSize.caption,
            fontWeight: 600,
          }}
        >
          + Add new store
        </button>
      </div>

      {/* Empty State */}
      {(!storeList || storeList.length === 0) && (
        <div
          style={{
            padding: theme.spacing.lg,
            textAlign: "center",
            color: theme.colors.secondaryText,
          }}
        >
          <p style={{ margin: 0, marginBottom: theme.spacing.md }}>
            No stores found
          </p>
          <button
            onClick={() => navigate({ to: "/store/create" })}
            style={{
              border: "none",
              cursor: "pointer",
              padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
              borderRadius: theme.radius.md,
              backgroundColor: theme.colors.primary,
              color: "#ffffff",
              fontSize: theme.fontSize.caption,
              fontWeight: 600,
            }}
          >
            Create a store
          </button>
        </div>
      )}

      {/* Store List */}
      {storeList && storeList.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.sm }}>
            {visibleStores?.map((store) => {
              const isActive = currentUser?._id === store._id;
              const initial = store.name?.charAt(0).toUpperCase() || "S";

              return (
                <div
                  key={store._id}
                  onClick={() => handleSwitchStore(store._id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                    cursor: isSwitching ? "not-allowed" : "pointer",
                    backgroundColor: isActive ? `${theme.colors.primary}08` : "transparent",
                    border: `1px solid ${isActive ? theme.colors.primary : theme.colors.border}40`,
                    transition: "all 0.2s",
                    opacity: isSwitching ? 0.6 : 1,
                  }}
                >
                  {/* Store Initial */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: isActive ? theme.colors.primary : theme.colors.border,
                      color: isActive ? appColors.surfaceBase : theme.colors.text,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      fontSize: theme.fontSize.h5,
                    }}
                  >
                    {initial}
                  </div>

                  {/* Store Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: theme.colors.text }}>
                      {store.name}
                    </div>
                    {store.address?.city && (
                      <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.secondaryText }}>
                        {store.address.city}
                      </div>
                    )}
                  </div>

                  {/* Radio Button */}
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      border: `2px solid ${isActive ? theme.colors.primary : theme.colors.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isActive && (
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: theme.colors.primary,
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show More / Show Less Button */}
          {hasMoreStores && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: theme.spacing.xs,
                width: "100%",
                marginTop: theme.spacing.md,
                padding: theme.spacing.sm,
                backgroundColor: "transparent",
                border: "none",
                color: theme.colors.primary,
                fontSize: theme.fontSize.body,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {isExpanded ? "Show less" : `Show ${storeList.length - 2} more`}
              <span style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                ▼
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

const StoreManagementSection = ({ theme }: { theme: any }) => {
  const navigate = useNavigate();

  const rows: { label: string; description: string; to: string }[] = [
    {
      label: "Business details",
      description: "Name, business type & category",
      to: "/store/edit/business",
    },
    {
      label: "Store address",
      description: "Location & address on map",
      to: "/store/edit/address",
    },
    {
      label: "Contact information",
      description: "Email & phone numbers",
      to: "/store/edit/contact",
    },
  ];

  return (
    <div
      style={{
        margin: theme.spacing.md,
        marginTop: 0,
        marginBottom: theme.spacing.md,
        backgroundColor: theme.colors.card,
        borderRadius: theme.radius.lg,
        padding: theme.spacing.md,
        boxShadow: `0 2px 8px ${theme.colors.border}20`,
        border: `1px solid ${theme.colors.border}`,
      }}
    >
      <h2
        style={{
          fontSize: theme.fontSize.h4,
          fontWeight: 600,
          margin: 0,
          marginBottom: theme.spacing.md,
          color: theme.colors.text,
        }}
      >
        Store details
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.sm }}>
        {rows.map((row) => (
          <button
            key={row.to}
            onClick={() => navigate({ to: row.to })}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: theme.spacing.md,
              padding: theme.spacing.md,
              borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.border}40`,
              backgroundColor: "transparent",
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
            }}
          >
            <div>
              <div style={{ fontWeight: 500, color: theme.colors.text }}>
                {row.label}
              </div>
              <div
                style={{
                  fontSize: theme.fontSize.caption,
                  color: theme.colors.secondaryText,
                }}
              >
                {row.description}
              </div>
            </div>
            <span style={{ color: theme.colors.secondaryText }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const GeneralSettingScreen = () => {
  const {theme} = useTheme();
  const fileUploaderRef = useRef<FileUploaderRef>(null);

  const currentUser = useSelector(currentUserSelector);

  // State for dropdowns
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSettingsSection, setActiveSettingsSection] =
    useState<SettingsSectionId | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<PrintSettingsFormData>({
    defaultValues: {
      printConfig: {
        billSize: "3-inc",
        logoUrl: "",
        includeStoreName: true,
        mobileApp: {
          printChoice: ChoiceEnum.AskEveryTime,
          printPreference: "Standard",
          numberOfCopies: 1,
        },
        webApp: {
          printChoice: ChoiceEnum.AskEveryTime,
          printPreference: "Standard",
          numberOfCopies: 1,
        },
        includeStoreAddress: {
          enabled: true,
          storeAddress: {
            line1: true,
            line2: true,
            landmark: false,
            locality: true,
            city: true,
            tehsil: false,
            district: true,
            state: true,
            country: false,
            postalCode: true,
          },
        },
        includePhoneNumber: {
          enabled: true,
          phoneNumber: (function() {
            if (!currentUser) return "";
            const rawLists = [
              ...(currentUser.phoneNumberList || []),
              ...(currentUser.phoneNumbers || []),
              ...(currentUser.phoneNumber && typeof currentUser.phoneNumber === "object" ? [currentUser.phoneNumber] : [])
            ];
            for (const p of rawLists) {
              if (p?.phoneNumber) return p.phoneNumber;
              if (p?.countryCode && p?.nationalNumber) return `${p.countryCode}${p.nationalNumber}`;
            }
            return "";
          })(),
        },
      },
      billQrCodeConfig: {
        enabled: true,
        type: BillQrCodeEnum.OrderUrl,
        redirectUrl: "",
      },
      billTermsAndConditions: [],
      billFooterMessage: "",
      barcodePrinterConfig: {
        logoUrl: "",
        useSameLogo: false,
      },
      saleOrderConfig: {
        showPurchasePrice: false,
      },
    },
    mode: "onBlur",
  });

  const getPhoneStrings = () => {
    if (!currentUser) return [];
    const phones = [];
    const rawLists = [
      ...(currentUser.phoneNumberList || []),
      ...(currentUser.phoneNumbers || []),
    ];
    if (currentUser.phoneNumber && typeof currentUser.phoneNumber === "object") {
      rawLists.push(currentUser.phoneNumber);
    }
    
    for (const p of rawLists) {
      if (!p) continue;
      let num = p.phoneNumber || "";
      if (!num && p.countryCode && p.nationalNumber) {
        num = `${p.countryCode}${p.nationalNumber}`;
      }
      if (num && !phones.includes(num)) {
        phones.push(num);
      }
    }
    return phones;
  };

  const [createStoreProfile] = useCreateStoreProfileMutation();
  const [updateStoreProfile] = useUpdateStoreProfileMutation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "billTermsAndConditions" as const ,
  });

  const { data: { profileDetail } = { profileDetail: {} } } =
    useStoreSelfQuery<StoreSelfResponse>(undefined, {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    });

  useEffect(() => {
    if (profileDetail) {
      const existingTerms = (profileDetail.printConfig?.termsAndConditions || profileDetail.billTermsAndConditions || []).filter(
        (term: string) => term && term.trim() !== ""
      );
      
      const existingFooterMessage = profileDetail.printConfig?.footerMessage ?? profileDetail.billFooterMessage ?? "";

      const baseQrConfig = profileDetail.printConfig?.qrCodeConfig || profileDetail.billQrCodeConfig;

      const formData: PrintSettingsFormData = {
        printConfig: {
          billSize: profileDetail.printConfig?.billSize ,
          logoUrl: profileDetail.printConfig?.logoUrl || "",
          includeStoreName: profileDetail.printConfig?.includeStoreName ?? true,
          mobileApp: {
            printChoice:
              profileDetail.printConfig?.mobileApp?.printChoice ||
              ChoiceEnum.AskEveryTime,
            printPreference:
              profileDetail.printConfig?.mobileApp?.printPreference ||
              "Standard",
            numberOfCopies:
              profileDetail.printConfig?.mobileApp?.numberOfCopies || 1,
          },
          webApp: {
            printChoice:
              profileDetail.printConfig?.webApp?.printChoice ||
              ChoiceEnum.AskEveryTime,
            printPreference:
              profileDetail.printConfig?.webApp?.printPreference || "Standard",
            numberOfCopies:
              profileDetail.printConfig?.webApp?.numberOfCopies || 1,
          },
          includeStoreAddress: {
            enabled: profileDetail.printConfig?.includeStoreAddress?.enabled ?? true,
            storeAddress: {
              line1: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.line1 ?? true,
              line2: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.line2 ?? true,
              landmark: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.landmark ?? false,
              locality: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.locality ?? true,
              city: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.city ?? true,
              tehsil: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.tehsil ?? false,
              district: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.district ?? true,
              state: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.state ?? true,
              country: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.country ?? false,
              postalCode: profileDetail.printConfig?.includeStoreAddress?.storeAddress?.postalCode ?? true,
            },
          },
          includePhoneNumber: {
            enabled: profileDetail.printConfig?.includePhoneNumber?.enabled ?? true,
            phoneNumber: profileDetail.printConfig?.includePhoneNumber?.phoneNumber || getPhoneStrings()[0] || "",
          },
        },
        billQrCodeConfig: {
          enabled: baseQrConfig?.enabled ?? true,
          type: baseQrConfig?.redirectUrl === "__PAYMENT_QR__"
            ? BillQrCodeEnum.PaymentUrl
            : (baseQrConfig?.type || BillQrCodeEnum.OrderUrl),
          redirectUrl: baseQrConfig?.redirectUrl === "__PAYMENT_QR__"
            ? ""
            : (baseQrConfig?.redirectUrl || ""),
        },
        billTermsAndConditions: existingTerms,
        billFooterMessage: existingFooterMessage,
        saleOrderConfig: {
          showPurchasePrice: profileDetail.saleOrderConfig?.showPurchasePrice ?? true,
        },
      };
      if (profileDetail.barcodePrinterConfig) {
        formData.barcodePrinterConfig = {
          stickerWidth: profileDetail.barcodePrinterConfig?.stickerWidth,
          stickerHeight: profileDetail.barcodePrinterConfig?.stickerHeight,
          stickersPerRow: profileDetail.barcodePrinterConfig?.stickersPerRow,
          orientation: profileDetail.barcodePrinterConfig?.orientation,
          logoUrl: profileDetail.barcodePrinterConfig?.logoUrl,
          includeProductCode: profileDetail.barcodePrinterConfig?.includeProductCode,
          includeProductName: profileDetail.barcodePrinterConfig?.includeProductName,
          includeStoreName: profileDetail.barcodePrinterConfig?.includeStoreName,
          includeSellingPrice: profileDetail.barcodePrinterConfig?.includeSellingPrice,
          includeMrp: profileDetail.barcodePrinterConfig?.includeMrp,
        };
      } else {
        formData.barcodePrinterConfig = { ...defaultBarcodeConfigPayload };
      }

      reset(formData);
    }
  }, [profileDetail]);

  async function CreateOrUpdateProfile(data: PrintSettingsFormData) {
    try {
      const qrConfig = data.billQrCodeConfig;
      const parsedQrConfig = qrConfig?.type === BillQrCodeEnum.PaymentUrl
          ? { ...qrConfig, type: BillQrCodeEnum.SocialUrl, redirectUrl: "__PAYMENT_QR__" }
          : qrConfig;
          
      const submitData = {
        ...data,
        billQrCodeConfig: parsedQrConfig,
        printConfig: {
          ...data.printConfig,
          qrCodeConfig: parsedQrConfig,
          termsAndConditions: data.billTermsAndConditions,
          footerMessage: data.billFooterMessage,
        },
        barcodePrinterConfig:
          data.barcodePrinterConfig?.stickerWidth &&
          data.barcodePrinterConfig?.stickerHeight &&
          data.barcodePrinterConfig?.stickersPerRow &&
          data.barcodePrinterConfig?.orientation
            ? (profileDetail?.barcodePrinterConfig
                ? !isEqual(data.barcodePrinterConfig, profileDetail.barcodePrinterConfig)
                : !isEqual(data.barcodePrinterConfig, defaultBarcodeConfigPayload))
              ? data.barcodePrinterConfig
              : undefined
            : undefined,
      };
      let response;
      if (profileDetail?._id) {
        response = await updateStoreProfile(submitData as any).unwrap();
      } else {
        response = await createStoreProfile(submitData as any).unwrap();
      }

      if (response) {
        toast.success(
          profileDetail
            ? "Settings updated successfully!"
            : "Settings created successfully!"
        );
      }
      return response;
    } catch (error) {
      toast.error(
        "Something went wrong: " +
          ((error as any)?.data?.message || "Please try again later.")
      );
    }
  }

  // Dropdown options
  const billSizeOptions = [
    { label: "3-inch", value: "3-inc" },
    { label: "2-inch", value: "2-inc" },
    { label: "A4", value: "A4" },
  ];

  const printChoiceOptions = [
    { label: "Ask Every Time", value: ChoiceEnum.AskEveryTime },
    { label: "Always Print", value: ChoiceEnum.AlwaysPrint },
    { label: "Never Print", value: ChoiceEnum.NeverPrint },
  ];

  const printPreferenceOptions = [
    { label: "Standard", value: "Standard" },
    { label: "Thermal", value: "Thermal" },
    { label: "PDF", value: "PDF" },
    { label: "Custom", value: "Custom" },
  ];

  const qrCodeTypeOptions = [
    { label: "Order URL", value: BillQrCodeEnum.OrderUrl },
    { label: "Social URL", value: BillQrCodeEnum.SocialUrl },
    { label: "Payment URL (UPI)", value: BillQrCodeEnum.PaymentUrl },
  ];

  const orientationOptions = [
    { label: "Portrait", value: OrientationEnum.Portrait },
    { label: "Landscape", value: OrientationEnum.Landscape },
  ];



  const phoneNumbersOptions = getPhoneStrings().map((num: string) => ({
    label: num.replace("(91)", "+91"),
    value: num,
  }));

  const stickersPerRowOptions = Array.from({ length: 6 }, (_, i) => i + 1).map(
    (count) => ({
      label: `${count} ${count === 1 ? "Sticker" : "Stickers"}`,
      value: count,
    })
  );

  const handleSelect = (field: string | undefined, value: any) => {
    if (!field) return;
    setValue(field as any, value, { shouldDirty: true });
    setActiveDropdown(null);
  };

  const handleDropdownClick = (
    field: string | undefined,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (!field) return;
    setActiveDropdown(activeDropdown === field ? null : field);
  };

  const barcodeConfig = watch("barcodePrinterConfig");

  // Dropdown component
  const Dropdown = ({
    options,
    value,
    field,
    placeholder = "Select",
  }: {
    options: { label: string; value: any }[];
    value: any;
    field: string;
    placeholder?: string;
  }) => {
    const isOpen = activeDropdown === field;
    const selectedOption = options.find((opt) => opt.value === value);

    return (
      <div style={{ position: "relative" }}>
        <button
          onClick={(e) => handleDropdownClick(field, e)}
          style={{
            border: "none",
            background: "none",
            color: theme.colors.primary,
            fontWeight: 600,
            fontSize: theme.fontSize.h6,
            cursor: "pointer",
            padding: "8px 16px",
            borderRadius: theme.radius.md,
            display: "flex",
            alignItems: "center",
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </button>

        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              backgroundColor: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              boxShadow: `0 4px 12px ${theme.colors.border}40`,
              zIndex: 1000,
              minWidth: "200px",
              maxHeight: "300px",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(field, option.value)}
                style={{
                  width: "100%",
                  border: "none",
                  background: "none",
                  padding: "12px 16px",
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: theme.fontSize.body,
                  color:
                    value === option.value
                      ? theme.colors.primary
                      : theme.colors.text,
                  backgroundColor:
                    value === option.value
                      ? `${theme.colors.primary}10`
                      : "transparent",
                  borderBottom: `1px solid ${theme.colors.border}20`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${theme.colors.primary}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    value === option.value
                      ? `${theme.colors.primary}10`
                      : "transparent";
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleFilesSelected = (files: string[]) => {
    if (files.length > 0) {
      setValue("printConfig.logoUrl", files[0], { shouldDirty: true });
      if (watch("barcodePrinterConfig.useSameLogo")) {
        setValue("barcodePrinterConfig.logoUrl", files[0], {
          shouldDirty: true,
        });
      }
    }
  };

  const settingsSections: Array<{
    id: SettingsSectionId;
    title: string;
    description: string;
  }> = [
    {
      id: "staff",
      title: "Staff Members",
      description: "Add staff and manage manager order permissions.",
    },
    {
      id: "bank",
      title: "Bank Details",
      description: "Manage UPI and bank account details.",
    },
    {
      id: "order",
      title: "Order Configuration",
      description: "Control order creation settings.",
    },
    {
      id: "print",
      title: "Print Bill Configuration",
      description: "Set bill size, logo, address, QR code, terms, and footer.",
    },
    {
      id: "barcode",
      title: "Barcode Configuration",
      description: "Configure sticker size, barcode logo, and label fields.",
    },
    {
      id: "printer",
      title: "Native Printer Settings",
      description: "Manage default local printer for OS-level printing.",
    },
  ];

  const activeSection = settingsSections.find(
    (section) => section.id === activeSettingsSection,
  );

  const renderSettingsMenu = () => (
    <div
      style={{
        margin: theme.spacing.md,
        marginTop: 0,
        backgroundColor: theme.colors.card,
        borderRadius: theme.radius.lg,
        padding: theme.spacing.md,
        boxShadow: `0 2px 8px ${theme.colors.border}20`,
        border: `1px solid ${theme.colors.border}`,
      }}
    >
      <div style={{ marginBottom: theme.spacing.md }}>
        <h2
          style={{
            fontSize: theme.fontSize.h4,
            fontWeight: 600,
            margin: 0,
            color: theme.colors.text,
          }}
        >
          Configuration
        </h2>
        <p
          style={{
            margin: `${theme.spacing.xs}px 0 0`,
            color: theme.colors.secondaryText,
            fontSize: theme.fontSize.caption,
          }}
        >
          Choose what you want to manage.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: theme.spacing.md,
        }}
      >
        {settingsSections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSettingsSection(section.id)}
            style={{
              border: `1px solid ${theme.colors.border}60`,
              backgroundColor: theme.colors.background,
              borderRadius: theme.radius.lg,
              padding: theme.spacing.md,
              textAlign: "left",
              cursor: "pointer",
              transition: "all 0.2s ease",
              minHeight: 108,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: theme.spacing.sm,
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    color: theme.colors.text,
                    fontSize: theme.fontSize.body,
                    fontWeight: 600,
                  }}
                >
                  {section.title}
                </h3>
                <p
                  style={{
                    margin: `${theme.spacing.xs}px 0 0`,
                    color: theme.colors.secondaryText,
                    fontSize: theme.fontSize.caption,
                    lineHeight: 1.5,
                  }}
                >
                  {section.description}
                </p>
              </div>
              <span
                style={{
                  color: theme.colors.primary,
                  fontSize: theme.fontSize.h4,
                  lineHeight: 1,
                }}
              >
                ›
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderActiveSectionHeader = () =>
    activeSection ? (
      <div
        style={{
          margin: theme.spacing.md,
          marginTop: 0,
          marginBottom: theme.spacing.md,
          backgroundColor: theme.colors.card,
          borderRadius: theme.radius.lg,
          padding: theme.spacing.md,
          boxShadow: `0 2px 8px ${theme.colors.border}20`,
          border: `1px solid ${theme.colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: theme.spacing.md,
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              color: theme.colors.text,
              fontSize: theme.fontSize.h4,
              fontWeight: 600,
            }}
          >
            {activeSection.title}
          </h2>
          <p
            style={{
              margin: `${theme.spacing.xs}px 0 0`,
              color: theme.colors.secondaryText,
              fontSize: theme.fontSize.caption,
            }}
          >
            {activeSection.description}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setActiveSettingsSection(null)}
          style={{
            border: `1px solid ${theme.colors.primary}`,
            background: theme.colors.primary,
            color: "white",
            cursor: "pointer",
            padding: "10px 16px",
            borderRadius: theme.radius.xxl,
            fontSize: theme.fontSize.body,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          ← Back
        </button>
      </div>
    ) : null;

  return (
    <div
      style={{ minHeight: "100vh", backgroundColor: theme.colors.background }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
          padding: "24px 24px 48px",
          boxSizing: "border-box",
        }}
      >
        {!activeSettingsSection && (
          <>
            {/* My Stores Section */}
            <MyStoresSection theme={theme} />
          </>
        )}

        {!activeSettingsSection && renderSettingsMenu()}

        {activeSettingsSection && renderActiveSectionHeader()}

        {/* Store Management Section */}
        {!activeSettingsSection && <StoreManagementSection theme={theme} />}

        {/* Bank Details Section */}
        {activeSettingsSection === "staff" && (
          <div style={{ margin: theme.spacing.md, marginTop: 0, marginBottom: theme.spacing.md }}>
            <StaffManagementScreen embedded />
          </div>
        )}

        {activeSettingsSection === "bank" && <BankDetailsSection />}

        {activeSettingsSection === "printer" && <NativePrinterSection theme={theme} />}

        {/* Order Configuration Section */}
        {activeSettingsSection === "order" && <div
          style={{
            margin: theme.spacing.md,
            marginTop: 0,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.md,
            boxShadow: `0 2px 8px ${theme.colors.border}20`,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: theme.spacing.lg,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text,
              }}
            >
              Order Configuration
            </h2>
          </div>

          {/* Show Purchase Price Toggle */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body, fontWeight: 500 }}>
              Show Purchase Price in New Order
            </span>
            <Controller
              control={control}
              name="saleOrderConfig.showPurchasePrice"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>
        </div>}

        {/* Print Configuration Section */}
        {activeSettingsSection === "print" && <div
          style={{
            margin: theme.spacing.md,
            marginTop: 0,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.md,
            boxShadow: `0 2px 8px ${theme.colors.border}20`,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: theme.spacing.lg,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text,
              }}
            >
              Print Bill Configuration
            </h2>
          </div>

          {/* Bill Size */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body, fontWeight: 500 }}>
              Bill Size
            </span>
            <Dropdown
              options={billSizeOptions}
              value={watch("printConfig.billSize")}
              field="printConfig.billSize"
            />
          </div>

          {/* Logo Upload */}
          <div
            style={{
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span
              style={{
                fontSize: theme.fontSize.body,
                fontWeight: 500,
                display: "block",
                marginBottom: theme.spacing.md,
              }}
            >
              Store Logo
            </span>
            <FileUploader
              ref={fileUploaderRef}
              type={FileTypeEnum.ProductImage}
              multiple={false}
              maxFiles={1}
              maxFileSize={5}
              allowedTypes={["image/*"]}
              onFilesSelected={handleFilesSelected}
              onError={(error) => toast.error(error)}
              images={watch("printConfig.logoUrl") ? [watch("printConfig.logoUrl") as string] : []}
            />
          </div>

          {/* Include Store Name */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Include Store Name
            </span>
            <Controller
              control={control}
              name="printConfig.includeStoreName"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* New specific fields for all bill sizes */}
          <>
            {/* Store Address Selection */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: `${theme.spacing.md}px 0`,
                  borderBottom: `1px solid ${theme.colors.border}40`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing.sm }}>
                  <span style={{ fontSize: theme.fontSize.body }}>
                    Include Store Address
                  </span>
                  <Controller
                    control={control}
                    name="printConfig.includeStoreAddress.enabled"
                    render={({ field: { onChange, value } }) => (
                      <label style={{ position: "relative", display: "inline-block", width: 50, height: 24 }}>
                        <input
                          type="checkbox"
                          checked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{ position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: value ? theme.colors.primary : theme.colors.border, transition: "0.4s", borderRadius: 24 }}>
                          <span style={{ position: "absolute", height: 16, width: 16, left: value ? "calc(100% - 20px)" : 4, bottom: 4, backgroundColor: appColors.surfaceBase, transition: "0.4s", borderRadius: "50%" }} />
                        </span>
                      </label>
                    )}
                  />
                </div>
                
                {watch("printConfig.includeStoreAddress.enabled") && (
                  <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.sm, marginTop: theme.spacing.sm, marginLeft: theme.spacing.lg }}>
                    {[
                      { key: "line1", label: "Line 1" },
                      { key: "line2", label: "Line 2" },
                      { key: "district", label: "District" },
                      { key: "city", label: "City" },
                      { key: "state", label: "State" },
                      { key: "postalCode", label: "Postal Code" },
                      { key: "country", label: "Country" },
                      { key: "landmark", label: "Landmark" },
                      { key: "locality", label: "Locality" },
                      { key: "tehsil", label: "Tehsil" },
                    ].filter(item => !!(currentUser as any)?.address?.[item.key]).map((item) => {
                      const addrVal = (currentUser as any)?.address?.[item.key];
                      return (
                        <label key={item.key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: theme.fontSize.body, cursor: "pointer" }}>
                          <Controller
                            control={control}
                            name={`printConfig.includeStoreAddress.storeAddress.${item.key}` as any}
                            render={({ field: { onChange, value } }) => (
                              <input
                                type="checkbox"
                                checked={!!value}
                                onChange={(e) => onChange(e.target.checked)}
                                style={{ width: "16px", height: "16px", accentColor: theme.colors.primary, cursor: "pointer" }}
                              />
                            )}
                          />
                          {item.label}{addrVal ? ` - ${addrVal}` : ""}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Store Phone Number Selection */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: `${theme.spacing.md}px 0`,
                  borderBottom: `1px solid ${theme.colors.border}40`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: watch("printConfig.includePhoneNumber.enabled") ? theme.spacing.sm : 0 }}>
                  <span style={{ fontSize: theme.fontSize.body }}>
                    Include Phone Number
                  </span>
                  <Controller
                    control={control}
                    name="printConfig.includePhoneNumber.enabled"
                    render={({ field: { onChange, value } }) => (
                      <label style={{ position: "relative", display: "inline-block", width: 50, height: 24 }}>
                        <input
                          type="checkbox"
                          checked={!!value}
                          onChange={(e) => onChange(e.target.checked)}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{ position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: value ? theme.colors.primary : theme.colors.border, transition: "0.4s", borderRadius: 24 }}>
                          <span style={{ position: "absolute", height: 16, width: 16, left: value ? "calc(100% - 20px)" : 4, bottom: 4, backgroundColor: appColors.surfaceBase, transition: "0.4s", borderRadius: "50%" }} />
                        </span>
                      </label>
                    )}
                  />
                </div>
                
                {watch("printConfig.includePhoneNumber.enabled") && phoneNumbersOptions.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing.sm, marginTop: theme.spacing.sm, marginLeft: theme.spacing.lg }}>
                    <span style={{ fontSize: theme.fontSize.body, marginBottom: 4 }}>Select Phone Number</span>
                    {phoneNumbersOptions.map((opt) => (
                      <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: theme.fontSize.body, cursor: "pointer" }}>
                        <Controller
                          control={control}
                          name="printConfig.includePhoneNumber.phoneNumber"
                          render={({ field: { onChange, value } }) => (
                            <input
                              type="radio"
                              checked={value === opt.value}
                              onChange={() => onChange(opt.value)}
                              style={{ width: "16px", height: "16px", accentColor: theme.colors.primary, cursor: "pointer" }}
                            />
                          )}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
                {watch("printConfig.includePhoneNumber.enabled") && phoneNumbersOptions.length === 0 && (
                  <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.secondaryText, marginLeft: theme.spacing.lg }}>
                    No phone numbers available for this store.
                  </div>
                )}
              </div>
            </>

          {/* Mobile App Print Settings */}
          <div style={{ marginTop: theme.spacing.md }}>
            <h3
              style={{
                fontSize: theme.fontSize.h5,
                marginBottom: theme.spacing.sm,
                fontWeight: 600,
                color: theme.colors.secondaryText,
              }}
            >
              Mobile App
            </h3>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${theme.spacing.sm}px 0`,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}
            >
              <span style={{ fontSize: theme.fontSize.body }}>
                Print Choice
              </span>
              <Dropdown
                options={printChoiceOptions}
                value={watch("printConfig.mobileApp.printChoice")}
                field="printConfig.mobileApp.printChoice"
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${theme.spacing.sm}px 0`,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}
            >
              <span style={{ fontSize: theme.fontSize.body }}>
                Print Preference
              </span>
              <Dropdown
                options={printPreferenceOptions}
                value={watch("printConfig.mobileApp.printPreference")}
                field="printConfig.mobileApp.printPreference"
              />
            </div>
          </div>

          {/* Web App Print Settings */}
          <div style={{ marginTop: theme.spacing.md }}>
            <h3
              style={{
                fontSize: theme.fontSize.h5,
                marginBottom: theme.spacing.sm,
                fontWeight: 600,
                color: theme.colors.secondaryText,
              }}
            >
              Web App
            </h3>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${theme.spacing.sm}px 0`,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}
            >
              <span style={{ fontSize: theme.fontSize.body }}>
                Print Choice
              </span>
              <Dropdown
                options={printChoiceOptions}
                value={watch("printConfig.webApp.printChoice")}
                field="printConfig.webApp.printChoice"
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${theme.spacing.sm}px 0`,
              }}
            >
              <span style={{ fontSize: theme.fontSize.body }}>
                Print Preference
              </span>
              <Dropdown
                options={printPreferenceOptions}
                value={watch("printConfig.webApp.printPreference")}
                field="printConfig.webApp.printPreference"
              />
            </div>
          </div>
        </div>}

        {/* QR Code Configuration Section */}
        {activeSettingsSection === "print" && <div
          style={{
            margin: theme.spacing.md,
            marginTop: theme.spacing.sm,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.md,
            boxShadow: `0 2px 8px ${theme.colors.border}20`,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: theme.spacing.md,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text,
              }}
            >
              QR Code Configuration
            </h2>
          </div>

          {/* QR Code Enabled */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Show QR Code in Bill
            </span>
            <Controller
              control={control}
              name="billQrCodeConfig.enabled"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* QR Code Type - Only show if QR code is enabled */}
          {watch("billQrCodeConfig.enabled") && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${theme.spacing.md}px 0`,
                borderBottom: `1px solid ${theme.colors.border}20`,
              }}
            >
              <span style={{ fontSize: theme.fontSize.body }}>
                QR Code Type
              </span>
              <Dropdown
                options={qrCodeTypeOptions}
                value={watch("billQrCodeConfig.type")}
                field="billQrCodeConfig.type"
              />
            </div>
          )}

          {/* Redirect URL - Only show if QR code is enabled and type is SocialUrl */}
          {watch("billQrCodeConfig.enabled") &&
            watch("billQrCodeConfig.type") === BillQrCodeEnum.SocialUrl && (
              <div style={{ padding: `${theme.spacing.md}px 0` }}>
                <span
                  style={{
                    marginBottom: theme.spacing.sm,
                    fontWeight: 500,
                    display: "block",
                  }}
                >
                  Redirect URL
                </span>
                <Controller
                  control={control}
                  name="billQrCodeConfig.redirectUrl"
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState: { error },
                  }) => (
                    <div>
                      <input
                        type="text"
                        value={value || ""}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="Enter URL"
                        style={{
                          width: "100%",
                          border: `1px solid ${
                            error ? theme.colors.error : theme.colors.border
                          }`,
                          borderRadius: theme.radius.md,
                          padding: theme.spacing.md,
                          fontSize: theme.fontSize.body,
                          backgroundColor: theme.colors.background,
                          color: theme.colors.text,
                          boxSizing: "border-box",
                        }}
                      />
                      {error && (
                        <div
                          style={{
                            color: theme.colors.error,
                            fontSize: theme.fontSize.caption,
                            marginTop: theme.spacing.xs,
                          }}
                        >
                          {error.message}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            )}
        </div>}

        {/* Barcode Configuration Section */}
        {activeSettingsSection === "barcode" && <div
          style={{
            margin: theme.spacing.md,
            marginTop: theme.spacing.sm,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.md,
            boxShadow: `0 2px 8px ${theme.colors.border}20`,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: theme.spacing.md,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text,
              }}
            >
              Barcode Configuration
            </h2>
          </div>

          {/* Sticker Width */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Sticker Width (mm)<span style={{ color: theme.colors.error }}>*</span>
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.stickerWidth"
              rules={{ required: "Width is compulsory" }}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => (
                <div style={{ alignItems: "flex-end" }}>
                  <input
                    type="number"
                    value={value || ""}
                    onChange={(e) => {
                      const val = e.target.valueAsNumber;
                      onChange(isNaN(val) ? "" : val);
                    }}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onBlur={onBlur}
                    placeholder="Width in mm"
                    style={{
                      width: 100,
                      border: `1px solid ${
                        error ? theme.colors.error : theme.colors.border
                      }`,
                      borderRadius: theme.radius.md,
                      padding: theme.spacing.sm,
                      fontSize: theme.fontSize.body,
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                      textAlign: "center",
                    }}
                  />
                  {error && (
                    <div
                      style={{
                        color: theme.colors.error,
                        fontSize: theme.fontSize.caption,
                        marginTop: theme.spacing.xs,
                      }}
                    >
                      {error.message}
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          {/* Sticker Height */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Sticker Height (mm) <span style={{ color: theme.colors.error }}>*</span>
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.stickerHeight"
              rules={{ required: "Height is compulsory" }}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => (
                <div style={{ alignItems: "flex-end" }}>
                  <input
                    type="number"
                    value={value || ""}
                    onChange={(e) => {
                      const val = e.target.valueAsNumber;
                      onChange(isNaN(val) ? "" : val);
                    }}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onBlur={onBlur}
                    placeholder="Height in mm"
                    style={{
                      width: 100,
                      border: `1px solid ${
                        error ? theme.colors.error : theme.colors.border
                      }`,
                      borderRadius: theme.radius.md,
                      padding: theme.spacing.sm,
                      fontSize: theme.fontSize.body,
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                      textAlign: "center",
                    }}
                  />
                  {error && (
                    <div
                      style={{
                        color: theme.colors.error,
                        fontSize: theme.fontSize.caption,
                        marginTop: theme.spacing.xs,
                      }}
                    >
                      {error.message}
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
              minHeight: 60,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Stickers Per Row
            </span>
            <Dropdown
              options={stickersPerRowOptions}
              value={barcodeConfig?.stickersPerRow}
              field="barcodePrinterConfig.stickersPerRow"
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              minHeight: 60,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>Orientation</span>
            <Dropdown
              options={orientationOptions}
              value={barcodeConfig?.orientation}
              field="barcodePrinterConfig.orientation"
            />
          </div>

          {/* Include Store Name */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Include Store Name
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.includeStoreName"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* Include Product Code */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Include Product Code
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.includeProductCode"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* Include Product Name */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Include Product Name
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.includeProductName"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* Include Selling Price */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Include Selling Price
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.includeSellingPrice"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* Include MRP */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>Include MRP</span>
            <Controller
              control={control}
              name="barcodePrinterConfig.includeMrp"
              render={({ field: { onChange, value } }) => (
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: 50,
                    height: 24,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: value
                        ? theme.colors.primary
                        : theme.colors.border,
                      transition: "0.4s",
                      borderRadius: 24,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        height: 16,
                        width: 16,
                        left: value ? "calc(100% - 20px)" : 4,
                        bottom: 4,
                        backgroundColor: appColors.surfaceBase,
                        transition: "0.4s",
                        borderRadius: "50%",
                      }}
                    />
                  </span>
                </label>
              )}
            />
          </div>

          {/* Use Same Logo as Bill */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: `${theme.spacing.md}px 0`,
              borderBottom: `1px solid ${theme.colors.border}40`,
            }}
          >
            <span style={{ fontSize: theme.fontSize.body }}>
              Use Same Logo as Bill
            </span>
            <Controller
              control={control}
              name="barcodePrinterConfig.useSameLogo"
               render={({ field: { onChange, value } }) => (
                 <label
                   style={{
                     position: "relative",
                     display: "inline-block",
                     width: 50,
                     height: 24,
                   }}
                 >
                  <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      onChange(checked);
                      if (checked) {
                        // copy print logo to barcode logo
                        const printLogo = watch("printConfig.logoUrl");
                        if (printLogo) {
                          setValue("barcodePrinterConfig.logoUrl", printLogo, {
                            shouldDirty: true,
                          });
                        }
                      } else {
                        // optional: clear barcode logo when user disables same-logo
                        // setValue("barcodePrinterConfig.logoUrl", "", { shouldDirty: true });
                      }
                    }}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                   <span
                     style={{
                       position: "absolute",
                       cursor: "pointer",
                       top: 0,
                       left: 0,
                       right: 0,
                       bottom: 0,
                       backgroundColor: value === true
                         ? theme.colors.primary
                         : theme.colors.border,
                       transition: "0.4s",
                       borderRadius: 24,
                     }}
                   >
                     <span
                       style={{
                         position: "absolute",
                         height: 16,
                         width: 16,
                         left: value === true ? "calc(100% - 20px)" : 4,
                         bottom: 4,
                         backgroundColor: appColors.surfaceBase,
                         transition: "0.4s",
                         borderRadius: "50%",
                       }}
                     />
                   </span>
                 </label>
               )}
             />
          </div>
          {/* Logo Uploader */}
            <div
              style={{
                padding: `${theme.spacing.md}px 0`,
                borderBottom: `1px solid ${theme.colors.border}40`,
              }}
            >
              <span
                style={{
                  fontSize: theme.fontSize.body,
                  fontWeight: 500,
                  display: "block",
                  marginBottom: theme.spacing.md,
                }}
              >
                Barcode Logo
              </span>
              <FileUploader
                ref={fileUploaderRef}
                type={FileTypeEnum.ProductImage}
                multiple={false}
                maxFiles={1}
                maxFileSize={5}
                allowedTypes={["image/*"]}
                onFilesSelected={(files) => {
                  if (files.length > 0) {
                    setValue("barcodePrinterConfig.logoUrl", files[0], {
                      shouldDirty: true,
                    });
                  }
                }}
                onError={(error) => toast.error(error)}
                images={
                  watch("barcodePrinterConfig.logoUrl")
                    ? [watch("barcodePrinterConfig.logoUrl") as string]
                    : []
                }
              />
            </div>
          
        </div>}

        {/* Bill Terms Section */}
        {activeSettingsSection === "print" && <div
          style={{
            margin: theme.spacing.md,
            marginTop: theme.spacing.sm,
            backgroundColor: theme.colors.card,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.md,
            boxShadow: `0 2px 8px ${theme.colors.border}20`,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: theme.spacing.md,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text,
              }}
            >
              Bill Terms and Conditions
            </h2>
          </div>

          {fields.length > 0 ? (
            fields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: theme.spacing.sm,
                  }}
                >
                  <span
                    style={{
                      fontSize: theme.fontSize.caption,
                      color: theme.colors.secondaryText,
                    }}
                  >
                    Term {index + 1}
                  </span>
                  <button
                    onClick={() => remove(index)}
                    style={{
                      border: "none",
                      background: "none",
                      color: theme.colors.error,
                      fontSize: theme.fontSize.caption,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
                <Controller
                  control={control}
                  name={`billTermsAndConditions.${index}`}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <textarea
                      value={value || ""}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder={`Enter terms and condition ${index + 1}`}
                      rows={4}
                      style={{
                        width: "100%",
                        border: `1px solid ${theme.colors.border}`,
                        borderRadius: theme.radius.md,
                        padding: theme.spacing.md,
                        fontSize: theme.fontSize.body,
                        backgroundColor: theme.colors.background,
                        color: theme.colors.text,
                        resize: "vertical",
                        minHeight: 100,
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                      }}
                    />
                  )}
                />
              </div>
            ))
          ) : (
            <div
              style={{
                color: theme.colors.secondaryText,
                fontSize: theme.fontSize.body,
                margin: `${theme.spacing.md}px 0`,
                marginLeft: theme.spacing.sm,
              }}
            >
              No terms and conditions added yet
            </div>
          )}

          <button
            onClick={() => append("")}
            style={{
              border: "none",
              background: "none",
              color: theme.colors.primary,
              cursor: "pointer",
              alignSelf: "flex-start",
              marginTop: theme.spacing.sm,
              fontSize: theme.fontSize.body,
            }}
          >
            + Terms and Conditions
          </button>

          {/* Footer Message */}
          <div
            style={{
              marginTop: theme.spacing.lg,
              borderTop: `1px solid ${theme.colors.border}`,
              paddingTop: theme.spacing.md,
            }}
          >
            <h2
              style={{
                fontSize: theme.fontSize.h4,
                fontWeight: 600,
                margin: 0,
                marginBottom: theme.spacing.md,
                color: theme.colors.text,
              }}
            >
              Footer Message
            </h2>

            <Controller
              control={control}
              name="billFooterMessage"
              render={({ field: { onChange, value, onBlur } }) => (
                <textarea
                  value={value || ""}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder="Enter footer message (e.g. Thank you for shopping with us!)"
                  rows={4}
                  style={{
                    width: "100%",
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.radius.md,
                    padding: theme.spacing.md,
                    fontSize: theme.fontSize.body,
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                    resize: "vertical",
                    minHeight: 100,
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              )}
            />
          </div>
        </div>}

        {/* Manual Save Button */}
        {activeSettingsSection &&
          !["staff", "bank"].includes(activeSettingsSection) && (
        <div
          style={{
            margin: theme.spacing.md,
            marginTop: theme.spacing.sm,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            onClick={handleSubmit(CreateOrUpdateProfile)}
            disabled={!isDirty}
            style={{
              width: "20%",
              padding: "12px 16px",
              backgroundColor: theme.colors.primary,
              color: appColors.surfaceBase,
              border: "none",
              borderRadius: theme.radius.xxl,
              fontSize: theme.fontSize.body,
              fontWeight: 600,
              cursor: isDirty ? "pointer" : "not-allowed",
              opacity: isDirty ? 1 : 0.6,
            }}
          >
            Save Settings
          </button>
        </div>
          )}
      </div>
    </div>
  );
};
